import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Icon,
  Link,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  FiCheckCircle,
  FiClipboard,
  FiDownload,
  FiEdit3,
  FiExternalLink,
  FiPlayCircle,
  FiTrash2,
} from 'react-icons/fi';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Alert } from '../../../../components/_components_0.2/Alert';
import { FilePicker } from '../../../../components/Global/UploadMultiple/FilePicker';
import { useAuth } from '../../../../hooks/auth';
import {
  ActionDraft,
  ActionKind,
  FACTORY_LABELS,
  OCCURRENCE_TYPE_LABELS,
  STATUS_COLOR_SCHEMES,
  STATUS_LABELS,
} from '../../../../utils/qualityControlConstants';
import {
  NonConformity,
  NonConformitySigner,
  SignNonConformityInput,
  getEvidenceUrl,
  qualityControlService,
} from '../../../../services/qualityControlService';
import { ActionRow, ActionsRepeater } from '../ActionsRepeater';
import { CompleteActionModal } from '../CompleteActionModal';
import { PageShell } from '../PageShell';
import { STATUS_BADGE_STYLE } from '../statusBadgeStyle';
import { SignatureModal, signatureFont } from '../SignatureModal';
import { useDemoMode } from '../useDemoMode';
import {
  formatDateBR,
  formatDateTimeBR,
  isNonConformityOverdue,
} from '../utils';

const NonConformityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const demo = useDemoMode();
  const { user } = useAuth();
  const toast = useToast();
  const confirmClose = useDisclosure();
  const confirmDelete = useDisclosure();
  const location = useLocation();
  // Explícito: useCan({ permissions: ['access_full'] }) devolve sempre true.
  // Mesmas regras da API (ensureQualityControlAdminAccess / ...Access).
  const isAdmin =
    user.permissions.includes('access_full') ||
    user.roles.includes('master_user');
  const canDelete = isAdmin;
  // Quem abriu / responsável sem a permissão do módulo só vê, assina e baixa
  // o PDF desta NC.
  const hasModuleAccess =
    isAdmin || user.permissions.includes('quality_control_access');
  const [signingAs, setSigningAs] = useState<NonConformitySigner | null>(null);
  const [signing, setSigning] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const autoOpenedSignature = useRef(false);
  const [completingActionId, setCompletingActionId] = useState<string | null>(
    null,
  );
  const [completingAction, setCompletingAction] = useState(false);

  const [nonConformity, setNonConformity] = useState<NonConformity | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transitioning, setTransitioning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingEvidenceFiles, setPendingEvidenceFiles] = useState<File[]>([]);
  const [uploadingEvidences, setUploadingEvidences] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await qualityControlService.getById(id, demo);
      setNonConformity(data);
    } catch {
      setError('Não foi possível carregar esta não conformidade.');
    } finally {
      setLoading(false);
    }
  }, [demo, id]);

  useEffect(() => {
    load();
  }, [load]);

  const editable = nonConformity ? nonConformity.status !== 'closed' : false;
  const canManage = editable && hasModuleAccess;
  const bothSigned = Boolean(
    nonConformity?.opener_signed_at && nonConformity?.responsible_signed_at,
  );
  const allActions = nonConformity
    ? [...nonConformity.corrective_actions, ...nonConformity.preventive_actions]
    : [];
  const allActionsDone = allActions.every(action => action.completed_at);
  const canConclude = bothSigned && allActionsDone;
  // Executar o plano: quem abriu, o responsável pela NC ou um master (a API
  // confere de novo). Ter só a permissão do módulo não basta.
  // O plano só é executado depois da ciência do responsável (a API exige).
  const planAwaitingCiencia = Boolean(
    nonConformity &&
      editable &&
      !nonConformity.responsible_signed_at &&
      allActions.some(action => !action.completed_at),
  );
  const canExecutePlan = Boolean(
    nonConformity &&
      editable &&
      nonConformity.responsible_signed_at &&
      (user.id === nonConformity.created_by_user_id ||
        user.id === nonConformity.responsible_user_id ||
        isAdmin),
  );
  const actionBeingCompleted =
    allActions.find(action => action.id === completingActionId) ?? null;
  // O botão aparece para quem vê a NC; se quem assina não é o usuário
  // logado, a janela pede o login e a senha do SAMASC dessa pessoa.
  const canSignOpener = Boolean(
    nonConformity && editable && !nonConformity.opener_signed_at,
  );
  const canSignResponsible = Boolean(
    nonConformity &&
      editable &&
      nonConformity.opener_signed_at &&
      !nonConformity.responsible_signed_at,
  );
  const signerAccessId =
    signingAs === 'opener'
      ? nonConformity?.created_by_user_id
      : nonConformity?.responsible_user_id;
  const signerName =
    (signingAs === 'opener'
      ? nonConformity?.created_by_user_name
      : nonConformity?.responsible) ?? '';
  const signingForSomeoneElse = signerAccessId !== user.id;

  // Vindo do cadastro (?assinar=1), quem abriu já assina o registro.
  useEffect(() => {
    if (
      !autoOpenedSignature.current &&
      canSignOpener &&
      user.id === nonConformity?.created_by_user_id &&
      new URLSearchParams(location.search).get('assinar') === '1'
    ) {
      autoOpenedSignature.current = true;
      setSigningAs('opener');
    }
  }, [canSignOpener, location.search, nonConformity, user.id]);

  const handleSign = useCallback(
    async (input: SignNonConformityInput) => {
      if (!nonConformity || !signingAs) return;
      setSigning(true);
      try {
        const updated = await qualityControlService.sign(
          nonConformity.id,
          signingAs,
          input,
          demo,
        );
        setNonConformity(updated);
        setSigningAs(null);
        toast({
          title:
            signingAs === 'opener' ? 'Registro assinado' : 'Ciência assinada',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (err) {
        toast({
          title: 'Não foi possível assinar',
          description: err instanceof Error ? err.message : 'Tente novamente.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setSigning(false);
      }
    },
    [demo, nonConformity, signingAs, toast],
  );

  const handleCompleteAction = useCallback(
    async (files: File[], note: string) => {
      if (!nonConformity || !completingActionId) return;
      setCompletingAction(true);
      try {
        // Um arquivo por vez, como as evidências da abertura; a API só
        // conclui com ao menos uma evidência já anexada à ação.
        await files.reduce(
          (previous, file) =>
            previous.then(async () => {
              await qualityControlService.uploadActionEvidence(
                nonConformity.id,
                completingActionId,
                file,
                demo,
              );
            }),
          Promise.resolve(),
        );

        const updated = await qualityControlService.completeAction(
          nonConformity.id,
          completingActionId,
          note,
          { id: user.id, name: user.name },
          demo,
        );
        setNonConformity(updated);
        setCompletingActionId(null);
        toast({
          title: 'Ação concluída',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (err) {
        toast({
          title: 'Não foi possível concluir a ação',
          description: err instanceof Error ? err.message : 'Tente novamente.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        // Arquivos que subiram antes do erro aparecem na janela.
        await load();
      } finally {
        setCompletingAction(false);
      }
    },
    [completingActionId, demo, load, nonConformity, toast, user],
  );

  const handleOpenCompleteAction = useCallback(
    (action: ActionRow) => setCompletingActionId(action.id ?? null),
    [],
  );

  const handleRemoveActionEvidence = useCallback(
    async (evidenceId: string) => {
      if (!nonConformity || !completingActionId) return;
      try {
        await qualityControlService.removeActionEvidence(
          nonConformity.id,
          completingActionId,
          evidenceId,
          demo,
        );
        await load();
      } catch (err) {
        toast({
          title: 'Não foi possível remover a evidência',
          description: err instanceof Error ? err.message : 'Tente novamente.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [completingActionId, demo, load, nonConformity, toast],
  );

  const handleDownloadPdf = useCallback(async () => {
    if (!nonConformity) return;
    if (demo) {
      toast({
        title: 'PDF indisponível na demonstração',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setDownloadingPdf(true);
    try {
      const blob = await qualityControlService.downloadPdf(nonConformity.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${nonConformity.code}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: 'Não foi possível gerar o PDF',
        description: 'Tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDownloadingPdf(false);
    }
  }, [demo, nonConformity, toast]);
  const overdue = nonConformity
    ? isNonConformityOverdue(nonConformity.status, nonConformity.due_date)
    : false;

  const handleAddAction = useCallback(
    async (kind: ActionKind, action: ActionDraft) => {
      if (!nonConformity) return;
      try {
        const updated = await qualityControlService.addAction(
          nonConformity.id,
          kind,
          action,
          demo,
        );
        setNonConformity(updated);
      } catch (err) {
        toast({
          title: 'Não foi possível adicionar a ação',
          description: err instanceof Error ? err.message : 'Tente novamente.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [demo, nonConformity, toast],
  );

  const handleUploadEvidences = useCallback(async () => {
    if (!nonConformity || pendingEvidenceFiles.length === 0) return;
    setUploadingEvidences(true);
    try {
      await Promise.all(
        pendingEvidenceFiles.map(file =>
          qualityControlService.uploadEvidence(nonConformity.id, file, demo),
        ),
      );
      setPendingEvidenceFiles([]);
      await load();
    } catch (err) {
      toast({
        title: 'Não foi possível enviar a evidência',
        description: err instanceof Error ? err.message : 'Tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUploadingEvidences(false);
    }
  }, [demo, load, nonConformity, pendingEvidenceFiles, toast]);

  const handleRemoveEvidence = useCallback(
    async (evidenceId: string) => {
      if (!nonConformity) return;
      try {
        await qualityControlService.removeEvidence(
          nonConformity.id,
          evidenceId,
          demo,
        );
        await load();
      } catch (err) {
        toast({
          title: 'Não foi possível remover a evidência',
          description: err instanceof Error ? err.message : 'Tente novamente.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [demo, load, nonConformity, toast],
  );

  const handleStartProgress = useCallback(async () => {
    if (!nonConformity) return;
    setTransitioning(true);
    try {
      const updated = await qualityControlService.updateStatus(
        nonConformity.id,
        'in_progress',
        { id: user.id, name: user.name },
        demo,
      );
      setNonConformity(updated);
    } catch (err) {
      toast({
        title: 'Não foi possível atualizar o status',
        description: err instanceof Error ? err.message : 'Tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setTransitioning(false);
    }
  }, [demo, nonConformity, toast, user]);

  const handleConclude = useCallback(async () => {
    if (!nonConformity) return;
    setTransitioning(true);
    let uploadedEvidences = false;
    try {
      // Concluída, a NC esconde o seletor de arquivos: o que ainda estava
      // só selecionado sumiria. Envia antes; se falhar, a NC não é concluída.
      if (pendingEvidenceFiles.length > 0) {
        await Promise.all(
          pendingEvidenceFiles.map(file =>
            qualityControlService.uploadEvidence(nonConformity.id, file, demo),
          ),
        );
        setPendingEvidenceFiles([]);
        uploadedEvidences = true;
      }

      const updated = await qualityControlService.updateStatus(
        nonConformity.id,
        'closed',
        { id: user.id, name: user.name },
        demo,
      );
      setNonConformity(updated);
      // Garante que os anexos recém-enviados apareçam na NC concluída.
      if (uploadedEvidences) await load();
      toast({
        title: 'Não conformidade concluída',
        description: 'Esta NC não pode mais ser alterada.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Não foi possível concluir a NC',
        description: err instanceof Error ? err.message : 'Tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      // Os anexos já subiram, mesmo sem concluir: mostra eles na tela.
      if (uploadedEvidences) await load();
    } finally {
      setTransitioning(false);
      confirmClose.onClose();
    }
  }, [
    confirmClose,
    demo,
    load,
    nonConformity,
    pendingEvidenceFiles,
    toast,
    user,
  ]);

  const handleDelete = useCallback(async () => {
    if (!nonConformity) return;
    setDeleting(true);
    try {
      await qualityControlService.delete(nonConformity.id, demo);
      toast({
        title: 'Não conformidade excluída',
        description: `${nonConformity.code} foi excluída permanentemente.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      history.push(`/quality-control/non-conformities${demo ? '?demo=1' : ''}`);
    } catch (err) {
      toast({
        title: 'Não foi possível excluir a NC',
        description: err instanceof Error ? err.message : 'Tente novamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
      confirmDelete.onClose();
    }
  }, [confirmDelete, demo, history, nonConformity, toast]);

  if (loading) {
    return (
      <PageShell>
        <Flex justify="center" py="10">
          <Spinner />
        </Flex>
      </PageShell>
    );
  }

  if (error || !nonConformity) {
    return (
      <PageShell>
        <Text color="red.400">
          {error || 'Não conformidade não encontrada.'}
        </Text>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Flex
        align="center"
        justify="space-between"
        mb="1"
        flexWrap="wrap"
        gridGap="3"
      >
        <Flex align="center" gridGap="3" flexWrap="wrap">
          <Icon as={FiClipboard} color="blue.400" boxSize="7" />
          <Heading size="lg" fontSize={{ base: 'xl', md: '3xl' }}>
            {nonConformity.code}
          </Heading>
          <Badge
            colorScheme={STATUS_COLOR_SCHEMES[nonConformity.status]}
            {...STATUS_BADGE_STYLE}
          >
            {STATUS_LABELS[nonConformity.status]}
          </Badge>
          {overdue && (
            <Badge colorScheme="red" {...STATUS_BADGE_STYLE}>
              Vencida
            </Badge>
          )}
          {demo && <Badge colorScheme="purple">Demonstração</Badge>}
        </Flex>

        <Flex
          gridGap="3"
          flexWrap="wrap"
          w={{ base: '100%', md: 'auto' }}
          direction={{ base: 'column', md: 'row' }}
        >
          <Button
            leftIcon={<FiDownload />}
            onClick={handleDownloadPdf}
            isLoading={downloadingPdf}
            variant="outline"
          >
            Baixar PDF
          </Button>
          {canManage && nonConformity.status === 'open' && (
            <Button
              leftIcon={<FiPlayCircle />}
              onClick={handleStartProgress}
              isLoading={transitioning}
              variant="outline"
              colorScheme="blue"
            >
              Iniciar Andamento
            </Button>
          )}
          {canManage && (
            <Button
              leftIcon={<FiCheckCircle />}
              onClick={confirmClose.onOpen}
              isLoading={transitioning}
              isDisabled={!canConclude}
              title={
                canConclude
                  ? undefined
                  : 'Faltam assinaturas ou ações do plano de ação'
              }
              colorScheme="green"
            >
              Concluir NC
            </Button>
          )}
          {canDelete && (
            <Button
              leftIcon={<FiTrash2 />}
              onClick={confirmDelete.onOpen}
              isLoading={deleting}
              variant="outline"
              colorScheme="red"
            >
              Excluir NC
            </Button>
          )}
        </Flex>
      </Flex>

      <Text fontSize="lg" fontWeight="600" mb="4" wordBreak="break-word">
        {nonConformity.title}
      </Text>

      {canManage && !canConclude && (
        <Text color="orange.300" fontSize="sm" mb="2">
          Para concluir esta NC, faltam{' '}
          {[
            !bothSigned && 'as assinaturas de quem abriu e do responsável',
            !allActionsDone && 'concluir todas as ações do plano de ação',
          ]
            .filter(Boolean)
            .join(' e ')}
          .
        </Text>
      )}

      {!editable && (
        <Text color="gray.400" mb="4">
          Concluída em {formatDateTimeBR(nonConformity.closed_at)} por{' '}
          {nonConformity.closed_by_user_name}. Esta não conformidade não pode
          mais ser alterada.
        </Text>
      )}

      <Box
        bg="gray.800"
        borderWidth="1px"
        borderColor="gray.700"
        borderRadius="lg"
        p={{ base: '4', md: '6' }}
        mt="4"
        mb="6"
      >
        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }}
          gridGap="4"
          mb="4"
        >
          <Box>
            <Text fontSize="xs" color="gray.400">
              Abertura da NC
            </Text>
            <Text>{formatDateBR(nonConformity.opened_at)}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400">
              Prazo para conclusão
            </Text>
            <Text>{formatDateBR(nonConformity.due_date)}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400">
              Fábrica
            </Text>
            <Text>{FACTORY_LABELS[nonConformity.factory_unit]}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400">
              Aberto por
            </Text>
            <Text>{nonConformity.created_by_user_name}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400">
              Responsável
            </Text>
            <Text>{nonConformity.responsible}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.400">
              Fornecedor ou Produtor
            </Text>
            <Text>{nonConformity.supplier_or_producer || '-'}</Text>
          </Box>
        </Box>

        <Box mb="4">
          <Text fontSize="xs" color="gray.400" mb={1}>
            Tipo de Ocorrência
          </Text>
          <Flex gridGap="2" flexWrap="wrap">
            {nonConformity.occurrence_types.map(type => (
              <Badge key={type}>{OCCURRENCE_TYPE_LABELS[type] || type}</Badge>
            ))}
          </Flex>
        </Box>

        <Box>
          <Text fontSize="xs" color="gray.400" mb={1}>
            Descrição e Causa da Não Conformidade
          </Text>
          <Text whiteSpace="pre-wrap">{nonConformity.description}</Text>
        </Box>
      </Box>

      <Box
        bg="gray.800"
        borderWidth="1px"
        borderColor="gray.700"
        borderRadius="lg"
        p={{ base: '4', md: '6' }}
        mb="6"
      >
        <Heading size="sm" mb={3}>
          Assinaturas
        </Heading>
        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
          gridGap="4"
        >
          {(['opener', 'responsible'] as NonConformitySigner[]).map(signer => {
            const signedAt = nonConformity[`${signer}_signed_at` as const];
            const signatureCpf =
              nonConformity[`${signer}_signature_cpf` as const];
            const personName =
              signer === 'opener'
                ? nonConformity.created_by_user_name
                : nonConformity.responsible;
            const canSign =
              signer === 'opener' ? canSignOpener : canSignResponsible;
            let pendingText = 'Pendente de assinatura';
            if (!editable) {
              pendingText = 'Não assinada';
            } else if (
              signer === 'responsible' &&
              !nonConformity.opener_signed_at
            ) {
              pendingText = 'Aguardando a assinatura de quem abriu';
            }

            return (
              <Box key={signer} bg="gray.700" borderRadius="md" p="4">
                <Text fontSize="xs" color="gray.400">
                  {signer === 'opener'
                    ? 'Registro (quem abriu)'
                    : 'Ciência do responsável'}
                </Text>
                <Text fontWeight="600" mb="2">
                  {personName}
                </Text>
                {signedAt ? (
                  <>
                    <Text
                      fontFamily={signatureFont(
                        nonConformity[`${signer}_signature_style` as const],
                      )}
                      fontSize="2xl"
                      color="blue.300"
                      wordBreak="break-word"
                    >
                      {nonConformity[`${signer}_signature_name` as const]}
                    </Text>
                    <Text fontSize="xs" color="gray.400">
                      {signatureCpf ? `CPF ${signatureCpf} · ` : ''}
                      Assinado em {formatDateTimeBR(signedAt)}
                    </Text>
                  </>
                ) : (
                  <Flex
                    align="center"
                    justify="space-between"
                    gridGap="2"
                    flexWrap="wrap"
                  >
                    <Text fontSize="sm" color="orange.300">
                      {pendingText}
                    </Text>
                    {canSign && (
                      <Button
                        size="sm"
                        leftIcon={<FiEdit3 />}
                        colorScheme="green"
                        onClick={() => setSigningAs(signer)}
                      >
                        {signer === 'opener'
                          ? 'Assinar registro'
                          : 'Assinar ciência'}
                      </Button>
                    )}
                  </Flex>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        bg="gray.800"
        borderWidth="1px"
        borderColor="gray.700"
        borderRadius="lg"
        p={{ base: '4', md: '6' }}
        mb="6"
      >
        <Heading size="sm" mb={3}>
          Evidências
        </Heading>
        {nonConformity.evidences.length > 0 && (
          <Flex direction="column" gridGap="2" mb="4">
            {nonConformity.evidences.map(evidence => (
              <Flex
                key={evidence.id}
                justify="space-between"
                align="center"
                bg="gray.700"
                borderRadius="md"
                p="3"
                gridGap="2"
              >
                <Link
                  href={getEvidenceUrl(evidence.url)}
                  isExternal
                  fontSize="sm"
                  color="blue.300"
                  wordBreak="break-all"
                  minW="0"
                  display="flex"
                  alignItems="center"
                  gridGap="2"
                >
                  <Icon as={FiExternalLink} flexShrink={0} />
                  {evidence.original_name}
                </Link>
                {canManage && (
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => handleRemoveEvidence(evidence.id)}
                  >
                    Remover
                  </Button>
                )}
              </Flex>
            ))}
          </Flex>
        )}
        {!canManage && nonConformity.evidences.length === 0 && (
          <Text fontSize="sm" color="gray.400">
            Nenhuma evidência anexada.
          </Text>
        )}
        {canManage && (
          <>
            <FilePicker
              id="detail-evidence-picker"
              files={pendingEvidenceFiles}
              onChange={setPendingEvidenceFiles}
            />
            {pendingEvidenceFiles.length > 0 && (
              <Button
                mt="3"
                size="sm"
                colorScheme="blue"
                w={{ base: '100%', md: 'auto' }}
                onClick={handleUploadEvidences}
                isLoading={uploadingEvidences}
              >
                Enviar evidências selecionadas
              </Button>
            )}
          </>
        )}
      </Box>

      <Divider mb="6" />

      {planAwaitingCiencia && (
        <Text color="orange.300" fontSize="sm" mb="4">
          O plano de ação só pode ser executado depois que o responsável assinar
          a ciência (e, antes dela, quem abriu assinar o registro).
        </Text>
      )}

      <Box mb="6">
        <ActionsRepeater
          title="Plano de Ação - Ações Corretivas"
          addButtonLabel="Adicionar Ação Corretiva"
          value={nonConformity.corrective_actions}
          onAdd={action => handleAddAction('corrective', action)}
          isDisabled={!canManage}
          canComplete={canExecutePlan}
          onComplete={handleOpenCompleteAction}
          isClosed={!editable}
          defaultResponsible={nonConformity.responsible}
        />
      </Box>

      <Box mb="6">
        <ActionsRepeater
          title="Plano de Ação - Ações Preventivas"
          addButtonLabel="Adicionar Ação Preventiva"
          value={nonConformity.preventive_actions}
          onAdd={action => handleAddAction('preventive', action)}
          isDisabled={!canManage}
          canComplete={canExecutePlan}
          onComplete={handleOpenCompleteAction}
          isClosed={!editable}
          defaultResponsible={nonConformity.responsible}
        />
      </Box>

      <CompleteActionModal
        isOpen={actionBeingCompleted !== null}
        onClose={() => setCompletingActionId(null)}
        action={actionBeingCompleted}
        isSubmitting={completingAction}
        onSubmit={handleCompleteAction}
        onRemoveEvidence={handleRemoveActionEvidence}
      />

      <SignatureModal
        isOpen={signingAs !== null}
        onClose={() => setSigningAs(null)}
        signer={signingAs ?? 'opener'}
        code={nonConformity.code}
        defaultName={signerName}
        requireCredentials={signingForSomeoneElse}
        loggedUserName={user.name}
        isSubmitting={signing}
        onSubmit={handleSign}
      />

      <Alert
        isOpen={confirmClose.isOpen}
        onClose={confirmClose.onClose}
        handleFunction={handleConclude}
        title="Concluir não conformidade"
        textBody={`Ao concluir a ${nonConformity.code}, ela não poderá mais ser alterada. Deseja continuar?`}
        ButonSave="Concluir"
        ButonClose="Cancelar"
      />

      <Alert
        isOpen={confirmDelete.isOpen}
        onClose={confirmDelete.onClose}
        handleFunction={handleDelete}
        title="Excluir não conformidade"
        textBody={`Esta ação é permanente e não pode ser desfeita. A ${nonConformity.code} e todo o seu histórico (ações, evidências) serão apagados. Deseja continuar?`}
        ButonSave="Excluir"
        ButonClose="Cancelar"
      />
    </PageShell>
  );
};

export default NonConformityDetail;
